// The Overview's topology model (spec 11 6.15.5): providers, their regions and attachments, the collapsing of large
// regions into a group card, and the tree items (names with the facts, levels, parents) the diagram renders as one
// `role="tree"`. Pure, so the keyboard model is testable without the DOM.
import type {
  AddressPlan,
  Attachment,
  AttachmentState,
  EntryPointStatus,
  Network,
  NetworkDns,
  NetworkKinds,
} from '../../api/types';
import { edgeOf, tierLabel, wafMode } from '../entryPoints/helpers';
import { attachmentPresentation, providerLabel } from '../shared';
import { strings } from '../../strings';

const p = strings.networks.page;
const tp = strings.networks.topology;
const ep = strings.networks.entryPoints;

/** Above this many cards a region collapses the rest into a group card. */
export const REGION_CARD_LIMIT = 8;
/** Above this many attachments the Overview opens on the List view. */
export const LIST_DEFAULT_ABOVE = 40;

export type TopologyItemKind =
  'entry-points' | 'entry-point' | 'provider' | 'region' | 'hub' | 'attachment' | 'group' | 'dns';

export interface TopologyItem {
  /** Unique in the tree; the selection key (`?select=`) for selectable items. */
  id: string;
  kind: TopologyItemKind;
  level: number;
  parentId: string | null;
  /** The accessible name with the facts ("orders-prod, westeurope, 10.65.4.0/22, medium, connected, isolated"). */
  name: string;
  children: string[];
  /** Expandable items (providers, regions with attachments) have children the tree can hide. */
  expandable: boolean;
  /** Whether Enter selects it (and opens the side panel). */
  selectable: boolean;
  provider?: string;
  region?: string;
  attachment?: Attachment;
  /** Group cards: the attachments they hold, and their states in words ("all connected"). */
  grouped?: Attachment[];
  summary?: string;
  /** Regions: their region blocks from the address plan. */
  blocks?: string[];
  /** Hubs (feature spec 17, phase 2): the hub block, the firewall's label, blocked spoke-to-spoke, linked hubs. */
  hub?: { cidr: string; firewall: string | null; blocked: boolean; linked: boolean };
  /** Entry points (feature spec 17, phase 3): the band's compact card and its route targets. */
  entryPoint?: {
    id: string;
    abbr: string;
    kind: string;
    tier: string | null;
    region: string | null;
    waf: 'prevention' | 'detection' | null;
    routes: number;
    pending: number;
    /** Deployment IDs of its routes' origins. */
    origins: string[];
  };
  /** Attachments: how many entry routes reach the deployment's apps. */
  entryRoutes?: number;
}

/** The entry point data the Overview adds to the tree (phase 3). */
export interface TopologyEntryPoints {
  kinds: NetworkKinds | undefined;
  statuses: ReadonlyMap<string, EntryPointStatus>;
}

export const entryPointKey = (id: string) => `entry-point/${id}`;

/** How many routes of the network's entry points reach a deployment's apps. */
export function entryRoutesOf(network: Pick<Network, 'settings'>, deploymentId: string): number {
  return network.settings.entryPoints.reduce(
    (n, x) => n + (x.routes ?? []).filter((r) => r.origin.deploymentId === deploymentId).length,
    0,
  );
}

/** How the Overview draws a provider's regions (feature spec 17, phase 2; spec 11 6.15.20). */
export interface TopologyShape {
  kind: Network['settings']['topology']['kind'];
  /** Regions have a hub card with one trunk line to their attachments. */
  hub: boolean;
  /** The firewall's label ("Azure Firewall Standard"), null without one. */
  firewall: string | null;
  /** `hub-spoke` with `blocked` spoke-to-spoke. */
  blocked: boolean;
  /** `mesh` without a hub: a dashed enclosure. */
  mesh: boolean;
  /** Attachments reach only requested traffic (a shield on each card). */
  requestsOnly: boolean;
  /** Hubs of different regions are connected. */
  crossRegion: boolean;
}

export function topologyShape(network: Pick<Network, 'settings'>, provider: string): TopologyShape {
  const settings = network.settings;
  const firewall = settings.providers[provider as 'azure']?.firewall;
  const firewallOn = !!firewall?.enabled;
  const kind = settings.topology.kind;
  const tier = firewall?.tier ? firewall.tier.charAt(0).toUpperCase() + firewall.tier.slice(1) : 'Standard';
  const hub = kind === 'hub-spoke' || firewallOn;
  return {
    kind,
    hub,
    firewall: firewallOn ? tp.firewallTier(tier) : null,
    blocked: kind === 'hub-spoke' && settings.topology.spokeToSpoke === 'blocked',
    mesh: kind === 'mesh' && !hub,
    requestsOnly: kind === 'micro-segmentation',
    crossRegion: hub && settings.topology.crossRegion === 'connected',
  };
}

/** The approved requests in and out of an attachment, in words ("2 approved requests"); null without any. */
export function requestWords(a: Attachment): string | null {
  const total = (a.requests?.in ?? 0) + (a.requests?.out ?? 0);
  return total > 0 ? tp.approvedRequests(total) : null;
}

export interface TopologyModel {
  items: Map<string, TopologyItem>;
  roots: string[];
}

/** Attachments of the network (retained blocks aren't attachments). */
export function totalAttachments(counts: Network['attachments']): number {
  return counts.connected + counts.pending + counts.connecting + counts.waitingForApply + counts.failed;
}

export const regionKey = (provider: string, region: string) => `network.region/${provider}/${region}`;
export const attachmentKey = (a: Pick<Attachment, 'deploymentId' | 'provider' | 'region'>) =>
  `network.attachment/${a.deploymentId}/${a.provider}/${a.region}`;
export const zonesKey = (provider: string) => `network.zones/${provider}`;
export const hubKey = (provider: string, region: string) => `hub/${provider}/${region}`;
export const ENTRY_POINTS_KEY = 'entry-points';
const providerKey = (provider: string) => `provider/${provider}`;
const groupKey = (provider: string, region: string) => `group/${provider}/${region}`;

/**
 * Whether an attachment can't connect until the network's next apply: it waits for its spoke or connect run while the
 * network was never applied (or its connect needs the apply first). The attachment JSON has no flag for it; the
 * network's `attachments.waitingForApply` counts them.
 */
export function waitsForNetworkApply(attachment: Attachment, network: Pick<Network, 'lastSuccessfulApply'>) {
  if (attachment.state !== 'pending' && attachment.state !== 'spoke-ready') return false;
  return !network.lastSuccessfulApply || attachment.lastError?.code === 'NETWORK_NOT_APPLIED';
}

const SORT_RANK: Record<AttachmentState, number> = {
  failed: 0,
  connecting: 1,
  'spoke-ready': 1,
  pending: 2,
  detaching: 3,
  detached: 4,
  connected: 5,
};

/** Cards that never collapse: failed, connecting, pending or waiting for the network's apply. */
export function neverCollapses(attachment: Attachment): boolean {
  return attachment.state !== 'connected';
}

/** The card's state in words ("Connected · isolated", "Pending · apply it first", "Waits for the network's apply"). */
export function cardStateWords(
  attachment: Attachment,
  network: Pick<Network, 'lastSuccessfulApply'>,
): string {
  if (waitsForNetworkApply(attachment, network)) return p.cardState.waits;
  if (attachment.state === 'connected') return p.cardState.connected(attachment.connectivity);
  return p.cardState[attachment.state];
}

export function sortCards(attachments: readonly Attachment[]): Attachment[] {
  return [...attachments].sort(
    (a, b) => SORT_RANK[a.state] - SORT_RANK[b.state] || a.deploymentName.localeCompare(b.deploymentName),
  );
}

/** "all connected" or "3 connected, 1 detaching". */
export function groupSummary(
  attachments: readonly Attachment[],
  network: Pick<Network, 'lastSuccessfulApply'>,
) {
  const counts = new Map<string, number>();
  for (const a of attachments) {
    const label = attachmentPresentation(a.state, waitsForNetworkApply(a, network)).label;
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  if (counts.size === 1) return p.allState([...counts.keys()][0]);
  return [...counts.entries()].map(([label, n]) => p.stateCount(n, label)).join(', ');
}

/** The cards a region shows: all when ≤ 8 or expanded, else the ones that never collapse first, then a group card. */
export function regionCards(
  attachments: readonly Attachment[],
  expanded: boolean,
): { shown: Attachment[]; grouped: Attachment[] } {
  const sorted = sortCards(attachments);
  if (expanded || sorted.length <= REGION_CARD_LIMIT) return { shown: sorted, grouped: [] };
  const fixed = sorted.filter(neverCollapses);
  const rest = sorted.filter((a) => !neverCollapses(a));
  const room = Math.max(0, REGION_CARD_LIMIT - 1 - fixed.length);
  return { shown: [...fixed, ...rest.slice(0, room)], grouped: rest.slice(room) };
}

/** The regions of the network, provider by provider, with their blocks and attachments. */
export function providerRegions(
  network: Network,
  attachments: readonly Attachment[],
  plan: AddressPlan | undefined,
): { provider: string; regions: { region: string; blocks: string[]; attachments: Attachment[] }[] }[] {
  const providers = new Map<string, Map<string, Attachment[]>>();
  for (const [provider, settings] of Object.entries(network.settings.providers)) {
    const regions = new Map<string, Attachment[]>();
    for (const region of settings?.regions ?? []) regions.set(region, []);
    providers.set(provider, regions);
  }
  for (const a of attachments) {
    if (!providers.has(a.provider)) providers.set(a.provider, new Map());
    const regions = providers.get(a.provider)!;
    if (!regions.has(a.region)) regions.set(a.region, []);
    regions.get(a.region)!.push(a);
  }
  return [...providers.entries()].map(([provider, regions]) => ({
    provider,
    regions: [...regions.entries()].map(([region, list]) => ({
      region,
      blocks: plan?.regions.find((r) => r.provider === provider && r.region === region)?.blocks ?? [],
      attachments: list,
    })),
  }));
}

export function attachmentName(
  a: Attachment,
  network: Pick<Network, 'lastSuccessfulApply'> & Partial<Pick<Network, 'settings'>>,
): string {
  const state = waitsForNetworkApply(a, network)
    ? p.cardState.waits
    : attachmentPresentation(a.state).label.toLowerCase();
  const requests = requestWords(a);
  const routes = network.settings ? entryRoutesOf({ settings: network.settings }, a.deploymentId) : 0;
  return p.attachmentName([
    a.deploymentName,
    a.region,
    a.cidr,
    a.size,
    state,
    ...(a.state === 'connected' || a.connectivity !== 'isolated' ? [a.connectivity] : []),
    ...(requests ? [requests] : []),
    ...(routes ? [ep.entryRoutes(routes)] : []),
  ]);
}

export function dnsSummary(dns: NetworkDns | undefined, internalZone: string | null | undefined) {
  const zones = dns?.zones.length ?? 0;
  const records = dns?.zones.reduce((sum, z) => sum + z.records.length, 0) ?? 0;
  return p.dnsSummary(zones, records, internalZone ?? null);
}

/** Builds the tree: Entry points, one item per provider (regions, their attachments and group cards), Private DNS. */
export function buildTopology(
  network: Network,
  attachments: readonly Attachment[],
  plan: AddressPlan | undefined,
  dns: NetworkDns | undefined,
  expandedGroups: ReadonlySet<string>,
  entryPoints: TopologyEntryPoints = { kinds: undefined, statuses: new Map() },
): TopologyModel {
  const items = new Map<string, TopologyItem>();
  const roots: string[] = [];
  const add = (item: TopologyItem) => {
    items.set(item.id, item);
    if (item.parentId) items.get(item.parentId)!.children.push(item.id);
    else roots.push(item.id);
  };
  const eps = network.settings.entryPoints;
  add({
    id: ENTRY_POINTS_KEY,
    kind: 'entry-points',
    level: 1,
    parentId: null,
    name: eps.length
      ? `${p.entryPoints}, ${ep.summary(
          eps.length,
          eps.reduce((n, x) => n + (x.routes?.length ?? 0), 0),
        )}`
      : `${p.entryPoints}, ${p.noEntryPoints}`,
    children: [],
    expandable: eps.length > 0,
    selectable: true,
  });
  for (const x of eps) {
    const kind = edgeOf(entryPoints.kinds, x.kind);
    const status = entryPoints.statuses.get(x.id);
    const pending = status ? status.pendingOrigins.length : 0;
    const waf = wafMode(x, kind);
    const kindLabel = kind?.label ?? x.kind;
    const tier = tierLabel(x, kind);
    add({
      id: entryPointKey(x.id),
      kind: 'entry-point',
      level: 2,
      parentId: ENTRY_POINTS_KEY,
      name: ep.treeItem(
        x.id,
        [kindLabel, tier].filter(Boolean).join(' '),
        ep.wafWords(waf),
        x.routes?.length ?? 0,
        pending,
      ),
      children: [],
      expandable: false,
      selectable: true,
      entryPoint: {
        id: x.id,
        abbr: kind?.abbr ?? x.kind.slice(0, 3).toUpperCase(),
        kind: kindLabel,
        tier,
        region: x.region ?? null,
        waf,
        routes: x.routes?.length ?? 0,
        pending,
        origins: [...new Set((x.routes ?? []).map((r) => r.origin.deploymentId))],
      },
    });
  }
  const providers = providerRegions(network, attachments, plan);
  for (const { provider, regions } of providers) {
    const label = providerLabel(provider);
    const pid = providerKey(provider);
    add({
      id: pid,
      kind: 'provider',
      level: 1,
      parentId: null,
      name: p.providerName(label, regions.length),
      children: [],
      expandable: regions.length > 0,
      selectable: false,
      provider,
    });
    for (const { region, blocks, attachments: list } of regions) {
      const rid = regionKey(provider, region);
      add({
        id: rid,
        kind: 'region',
        level: 2,
        parentId: pid,
        name: p.regionName(region, blocks.join(', '), list.length),
        children: [],
        expandable: list.length > 0,
        selectable: true,
        provider,
        region,
        blocks,
      });
      const shape = topologyShape(network, provider);
      if (shape.hub) {
        const cidr =
          plan?.regions.find((r) => r.provider === provider && r.region === region)?.hub?.cidr ?? '';
        const linked = shape.crossRegion && regions.length > 1;
        add({
          id: hubKey(provider, region),
          kind: 'hub',
          level: 3,
          parentId: rid,
          name: tp.hubName(region, cidr, shape.firewall, shape.blocked, linked),
          children: [],
          expandable: false,
          selectable: false,
          provider,
          region,
          hub: { cidr, firewall: shape.firewall, blocked: shape.blocked, linked },
        });
      }
      const { shown, grouped } = regionCards(list, expandedGroups.has(rid));
      for (const a of shown)
        add({
          id: attachmentKey(a),
          kind: 'attachment',
          level: 3,
          parentId: rid,
          name: attachmentName(a, network),
          children: [],
          expandable: false,
          selectable: true,
          provider,
          region,
          attachment: a,
          entryRoutes: entryRoutesOf(network, a.deploymentId),
        });
      if (grouped.length)
        add({
          id: groupKey(provider, region),
          kind: 'group',
          level: 3,
          parentId: rid,
          name: p.moreName(grouped.length, groupSummary(grouped, network)),
          children: [],
          expandable: false,
          selectable: false,
          provider,
          region,
          grouped,
          summary: groupSummary(grouped, network),
        });
    }
  }
  const firstProvider = providers[0]?.provider ?? 'azure';
  add({
    id: zonesKey(firstProvider),
    kind: 'dns',
    level: 1,
    parentId: null,
    name: `${p.privateDns}, ${dnsSummary(dns, network.settings.dns.internalZone)}`,
    children: [],
    expandable: false,
    selectable: true,
    provider: firstProvider,
  });
  return { items, roots };
}

/** The items the tree shows, in order (children of collapsed items left out). */
export function visibleItems(model: TopologyModel, collapsed: ReadonlySet<string>): TopologyItem[] {
  const result: TopologyItem[] = [];
  const walk = (id: string) => {
    const item = model.items.get(id)!;
    result.push(item);
    if (!collapsed.has(id)) for (const child of item.children) walk(child);
  };
  for (const root of model.roots) walk(root);
  return result;
}

export type TreeAction =
  | { type: 'focus'; id: string }
  | { type: 'expand'; id: string }
  | { type: 'collapse'; id: string }
  | { type: 'select'; id: string }
  | { type: 'open-group'; id: string }
  | null;

/** The tree's keyboard model (APG tree view): Up/Down, Right, Left, Home/End, Enter. */
export function treeKey(
  model: TopologyModel,
  collapsed: ReadonlySet<string>,
  current: string,
  key: string,
): TreeAction {
  const visible = visibleItems(model, collapsed);
  const index = visible.findIndex((item) => item.id === current);
  const item = model.items.get(current);
  if (index < 0 || !item) return visible[0] ? { type: 'focus', id: visible[0].id } : null;
  switch (key) {
    case 'ArrowDown':
      return index < visible.length - 1 ? { type: 'focus', id: visible[index + 1].id } : null;
    case 'ArrowUp':
      return index > 0 ? { type: 'focus', id: visible[index - 1].id } : null;
    case 'Home':
      return { type: 'focus', id: visible[0].id };
    case 'End':
      return { type: 'focus', id: visible[visible.length - 1].id };
    case 'ArrowRight':
      if (!item.expandable) return null;
      if (collapsed.has(item.id)) return { type: 'expand', id: item.id };
      return item.children[0] ? { type: 'focus', id: item.children[0] } : null;
    case 'ArrowLeft':
      if (item.expandable && !collapsed.has(item.id)) return { type: 'collapse', id: item.id };
      return item.parentId ? { type: 'focus', id: item.parentId } : null;
    case 'Enter':
      if (item.kind === 'group') return { type: 'open-group', id: item.id };
      if (item.selectable) return { type: 'select', id: item.id };
      if (item.expandable) return { type: collapsed.has(item.id) ? 'expand' : 'collapse', id: item.id };
      return null;
    default:
      return null;
  }
}
